/**
 * Meeting Provider Settings Component
 * BYOP (Bring Your Own Provider) configuration for video meeting providers
 * Supports: Zoom, Google Meet, Microsoft Teams, Jitsi
 */

import React, { useState, useEffect } from 'react';
import {
  Video,
  Settings,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface MeetingProvider {
  id: number;
  provider_type: string;
  provider_name: string | null;
  is_enabled: boolean;
  is_default: boolean;
  has_oauth_credentials: boolean;
  oauth_configured_at: string | null;
  jitsi_server_url: string | null;
  meetings_created: number;
  last_used_at: string | null;
}

interface MeetingProviderSettingsProps {
  organizationId: number;
  onSettingsChange?: () => void;
}

const PROVIDER_INFO = {
  zoom: {
    name: 'Zoom',
    icon: '📹',
    description: 'Video conferencing and webinars',
    setupUrl: 'https://marketplace.zoom.us/develop/create',
    setupInstructions: [
      'Go to Zoom Marketplace Developer Portal',
      'Create a new OAuth App',
      'Set Redirect URI to your callback URL',
      'Copy Client ID and Client Secret',
    ],
    requiredFields: ['client_id', 'client_secret'],
  },
  google_meet: {
    name: 'Google Meet',
    icon: '🎥',
    description: 'Google Calendar and Meet integration',
    setupUrl: 'https://console.cloud.google.com/apis/credentials',
    setupInstructions: [
      'Go to Google Cloud Console',
      'Create OAuth 2.0 credentials',
      'Enable Calendar API and Google Meet API',
      'Set Authorized redirect URIs',
      'Copy Client ID and Client Secret',
    ],
    requiredFields: ['client_id', 'client_secret'],
  },
  teams: {
    name: 'Microsoft Teams',
    icon: '💼',
    description: 'Microsoft 365 calendar and Teams meetings',
    setupUrl: 'https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps',
    setupInstructions: [
      'Go to Azure Portal > App registrations',
      'Register a new application',
      'Add redirect URI',
      'Create a client secret',
      'Grant API permissions for Calendar and OnlineMeetings',
    ],
    requiredFields: ['client_id', 'client_secret'],
  },
  jitsi: {
    name: 'Jitsi Meet',
    icon: '🎙️',
    description: 'Open-source video conferencing with optional JaaS integration',
    setupUrl: 'https://jaas.8x8.vc/',
    setupInstructions: [
      'Default: Use public meet.jit.si (no setup needed)',
      'Self-hosted: Enter your Jitsi server URL',
      'JaaS (8x8): Get API ID and secret from jaas.8x8.vc',
      'JWT enables: recording, moderation, branding',
    ],
    requiredFields: [],
  },
};

export function MeetingProviderSettings({
  organizationId,
  onSettingsChange,
}: MeetingProviderSettingsProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [providers, setProviders] = useState<MeetingProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  // Form state for configuring providers
  const [formData, setFormData] = useState<Record<string, {
    client_id: string;
    client_secret: string;
    redirect_uri: string;
    jitsi_server_url?: string;
    jitsi_jwt_app_id?: string;
    jitsi_jwt_secret?: string;
  }>>({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(cboId && { 'X-CBO-ID': cboId }),
    };
  };

  useEffect(() => {
    fetchProviders();
  }, [organizationId]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        getWeaveinApiUrl('/api/byop/meeting-providers'),
        {
          headers: getAuthHeaders(),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
      } else {
        setError('Failed to load meeting providers');
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
      setError('Failed to load meeting providers');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async (providerType: string) => {
    const data = formData[providerType];
    if (!data) return;

    try {
      setSaving(true);

      const baseUrl = getWeaveinApiUrl('/api');
      // Calculate redirect URI based on provider
      const redirectUri = data.redirect_uri ||
        `${baseUrl}/oauth/${providerType === 'google_meet' ? 'google' : providerType}/callback`;

      const response = await fetch(
        getWeaveinApiUrl('/api/byop/meeting-providers'),
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            provider_type: providerType,
            oauth_client_id: data.client_id.trim(),
            oauth_client_secret: data.client_secret.trim(),
            oauth_redirect_uri: redirectUri.trim(),
            is_enabled: true,
          }),
        }
      );

      if (response.ok) {
        fetchProviders();
        onSettingsChange?.();
        setExpandedProvider(null);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save credentials');
      }
    } catch (err) {
      console.error('Error saving credentials:', err);
      setError('Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveJitsiConfig = async (providerType: string) => {
    const data = formData[providerType];

    try {
      setSaving(true);

      const response = await fetch(
        getWeaveinApiUrl('/api/byop/meeting-providers'),
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            provider_type: providerType,
            jitsi_server_url: data?.jitsi_server_url || null,
            jitsi_jwt_app_id: data?.jitsi_jwt_app_id || null,
            jitsi_jwt_secret: data?.jitsi_jwt_secret || null,
            is_enabled: true,
          }),
        }
      );

      if (response.ok) {
        fetchProviders();
        onSettingsChange?.();
        setExpandedProvider(null);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save Jitsi configuration');
      }
    } catch (err) {
      console.error('Error saving Jitsi config:', err);
      setError('Failed to save Jitsi configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCredentials = async (providerType: string) => {
    if (!confirm('Are you sure you want to remove these credentials?')) return;

    try {
      setSaving(true);
      const response = await fetch(
        getWeaveinApiUrl(`/api/byop/meeting-providers/${providerType}`),
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        fetchProviders();
        onSettingsChange?.();
      }
    } catch (err) {
      console.error('Error removing credentials:', err);
    } finally {
      setSaving(false);
    }
  };

  const getProviderStatus = (providerType: string) => {
    return providers.find(p => p.provider_type === providerType);
  };

  const renderProviderCard = (type: string) => {
    const info = PROVIDER_INFO[type as keyof typeof PROVIDER_INFO];
    const status = getProviderStatus(type);
    const isExpanded = expandedProvider === type;
    const form = formData[type] || { client_id: '', client_secret: '', redirect_uri: '' };

    return (
      <div
        key={type}
        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
          onClick={() => setExpandedProvider(isExpanded ? null : type)}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{info.icon}</span>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">{info.name}</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">{info.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {status?.has_oauth_credentials ? (
              <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                <CheckCircle className="h-4 w-4" />
                Configured
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sm text-gray-400">
                <AlertCircle className="h-4 w-4" />
                Not configured
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
            {/* Setup Instructions */}
            <div className="mb-4">
              <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                Setup Instructions
              </h5>
              <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-1 list-decimal list-inside">
                {info.setupInstructions.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
              <a
                href={info.setupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline mt-2"
              >
                Open {info.name} Developer Console
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            {/* Credentials Form */}
            {type !== 'jitsi' ? (
              <div className="space-y-4">
                {/* Show message if credentials already saved */}
                {status?.has_oauth_credentials && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-2">
                    <p className="text-sm text-green-700 dark:text-green-300">
                      <CheckCircle className="h-4 w-4 inline mr-2" />
                      <strong>Credentials are saved securely.</strong> For security, stored credentials are not displayed.
                      Enter new values below only if you want to update them.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client ID
                  </label>
                  <input
                    type="text"
                    value={form.client_id}
                    onChange={(e) => setFormData({
                      ...formData,
                      [type]: { ...form, client_id: e.target.value }
                    })}
                    placeholder={status?.has_oauth_credentials ? "Credentials saved - enter new value to update" : "Enter OAuth Client ID (e.g., abc123.apps.provider.com)"}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This is the OAuth App Client ID from {info.name}'s developer console (not your email)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client Secret
                  </label>
                  <div className="relative">
                    <input
                      type={showSecrets[type] ? 'text' : 'password'}
                      value={form.client_secret}
                      onChange={(e) => setFormData({
                        ...formData,
                        [type]: { ...form, client_secret: e.target.value }
                      })}
                      placeholder={status?.has_oauth_credentials ? "Credentials saved - enter new value to update" : "Enter OAuth Client Secret"}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecrets({ ...showSecrets, [type]: !showSecrets[type] })}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showSecrets[type] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Redirect URI (auto-generated)
                  </label>
                  <input
                    type="text"
                    value={form.redirect_uri || `${getWeaveinApiUrl('/api')}/oauth/${type === 'google_meet' ? 'google' : type}/callback`}
                    onChange={(e) => setFormData({
                      ...formData,
                      [type]: { ...form, redirect_uri: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Add this URL to your OAuth app's redirect URIs
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    {status?.has_oauth_credentials && (
                      <button
                        onClick={() => handleRemoveCredentials(type)}
                        disabled={saving}
                        className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove Credentials
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => handleSaveCredentials(type)}
                    disabled={saving || !form.client_id || !form.client_secret}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {saving && <RefreshCw className="h-4 w-4 animate-spin" />}
                    {status?.has_oauth_credentials ? 'Update Credentials' : 'Save Credentials'}
                  </button>
                </div>
              </div>
            ) : (
              /* Jitsi - Optional JaaS/Self-hosted configuration */
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 mb-4">
                  <p className="text-sm text-green-700 dark:text-green-300">
                    <strong>Default:</strong> Works out of the box with meet.jit.si (free, no setup).
                    Configure below only for self-hosted or JaaS (8x8) premium features.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Jitsi Server URL (optional)
                  </label>
                  <input
                    type="text"
                    value={form.jitsi_server_url || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      [type]: { ...form, jitsi_server_url: e.target.value }
                    })}
                    placeholder="https://meet.jit.si (default)"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty to use the default public server
                  </p>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    JaaS / JWT Authentication (Optional)
                  </h5>
                  <p className="text-xs text-gray-500 mb-3">
                    Enable premium features like recording, transcription, and meeting moderation
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        JWT App ID
                      </label>
                      <input
                        type="text"
                        value={form.jitsi_jwt_app_id || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          [type]: { ...form, jitsi_jwt_app_id: e.target.value }
                        })}
                        placeholder="vpaas-magic-cookie-xxxxx..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        JWT Secret
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets[type] ? 'text' : 'password'}
                          value={form.jitsi_jwt_secret || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            [type]: { ...form, jitsi_jwt_secret: e.target.value }
                          })}
                          placeholder="Your JWT signing secret"
                          className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets({ ...showSecrets, [type]: !showSecrets[type] })}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets[type] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    {(status?.jitsi_server_url || status?.has_oauth_credentials) && (
                      <button
                        onClick={() => handleRemoveCredentials(type)}
                        disabled={saving}
                        className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        Reset to Defaults
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => handleSaveJitsiConfig(type)}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {saving && <RefreshCw className="h-4 w-4 animate-spin" />}
                    Save Configuration
                  </button>
                </div>
              </div>
            )}

            {/* Status Info */}
            {status?.has_oauth_credentials && status.oauth_configured_at && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Last configured: {fmt.dateTime(status.oauth_configured_at)}
                </p>
                {status.meetings_created > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Meetings created: {status.meetings_created}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Video className="h-5 w-5 text-blue-600" />
          Meeting Providers
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Configure your organization's video meeting integrations. Enter your OAuth app credentials to enable scheduling.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Provider Cards */}
      <div className="space-y-4">
        {Object.keys(PROVIDER_INFO).map(renderProviderCard)}
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
          Why BYOP (Bring Your Own Provider)?
        </h4>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          By using your organization's own OAuth apps, you maintain full control over your meeting integrations,
          branding, and usage limits. Each organization can configure their own provider accounts.
        </p>
      </div>
    </div>
  );
}

export default MeetingProviderSettings;
